'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Building2,
  ShieldAlert,
  TrendingUp,
  FileText,
  AlertTriangle,
  ShieldCheck,
  Clock,
  ClipboardList,
} from 'lucide-react';
import { VENDORS, ISSUES } from '@/lib/mockData';
import Icon from '@/components/ui/AppIcon';


// Backend integration point: replace with API call to /api/dashboard/kpis
function computeKPIs() {
  const totalVendors = VENDORS.length;
  const criticalVendors = VENDORS.filter((v) => v.criticality === 'Critical').length;
  const highRiskVendors = VENDORS.filter((v) => v.riskTier === 'High' || v.riskTier === 'Critical').length;
  const pendingIntake = VENDORS.filter((v) => v.lifecycleStage === 'Intake').length;
  const ddInProgress = VENDORS.filter((v) => v.ddStatus === 'In Progress' || v.ddStatus === 'Overdue').length;

  // Contracts expiring relative to today
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const in30 = new Date(now); in30.setDate(in30.getDate() + 30);
  const in60 = new Date(now); in60.setDate(in60.getDate() + 60);
  const in90 = new Date(now); in90.setDate(in90.getDate() + 90);

  const expiring30 = VENDORS.filter((v) => {
    if (!v.contractEnd) return false;
    const d = new Date(v.contractEnd);
    return d >= now && d <= in30;
  }).length;
  const expiring60 = VENDORS.filter((v) => {
    if (!v.contractEnd) return false;
    const d = new Date(v.contractEnd);
    return d > in30 && d <= in60;
  }).length;
  const expiring90 = VENDORS.filter((v) => {
    if (!v.contractEnd) return false;
    const d = new Date(v.contractEnd);
    return d > in60 && d <= in90;
  }).length;

  const openCritical = ISSUES.filter((i) => i.severity === 'Critical' && i.status !== 'Closed').length;
  const openHigh = ISSUES.filter((i) => i.severity === 'High' && i.status !== 'Closed').length;
  const missingInsurance = VENDORS.filter((v) => v.insuranceStatus === 'Missing' || v.insuranceStatus === 'Expired').length;
  const overdueReassessments = VENDORS.filter((v) => v.ddStatus === 'Overdue').length;

  return {
    totalVendors,
    criticalVendors,
    highRiskVendors,
    pendingIntake,
    ddInProgress,
    expiring30,
    expiring60,
    expiring90,
    openCritical,
    openHigh,
    missingInsurance,
    overdueReassessments,
  };
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtext?: string;
  icon: React.ElementType;
  variant: 'neutral' | 'critical' | 'warning' | 'positive' | 'info';
  href?: string;
  className?: string;
}

function KPICard({ title, value, subtext, icon: Icon, variant, href, className = '' }: KPICardProps) {
  const variantStyles = {
    neutral: 'bg-[hsl(var(--surface))] border-[hsl(var(--border))]',
    critical: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900',
    warning: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900',
    positive: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900',
    info: 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900',
  };
  const iconStyles = {
    neutral: 'text-[hsl(var(--text-muted))]',
    critical: 'text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400',
    positive: 'text-emerald-600 dark:text-emerald-400',
    info: 'text-blue-600 dark:text-blue-400',
  };
  const valueStyles = {
    neutral: 'text-[hsl(var(--text-primary))]',
    critical: 'text-red-700 dark:text-red-400',
    warning: 'text-amber-700 dark:text-amber-400',
    positive: 'text-emerald-700 dark:text-emerald-400',
    info: 'text-blue-700 dark:text-blue-400',
  };

  const content = (
    <div className={`rounded-lg border p-3.5 ${variantStyles[variant]} ${className} transition-all duration-150 hover:shadow-sm`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-[hsl(var(--text-muted))] leading-none mb-2 uppercase tracking-wide">
            {title}
          </p>
          <p className={`text-2xl font-bold tabular-nums leading-none ${valueStyles[variant]}`}>
            {value}
          </p>
          {subtext && (
            <p className="text-xs text-[hsl(var(--text-muted))] mt-1.5 leading-snug">
              {subtext}
            </p>
          )}
        </div>
        <div className={`p-1.5 rounded-md flex-shrink-0 ${iconStyles[variant]}`}>
          <Icon size={18} />
        </div>
      </div>
    </div>
  );

  if (href) {
    return <Link href={href} className="block">{content}</Link>;
  }
  return content;
}

export default function DashboardKPIs() {
  const [kpis, setKpis] = useState(() => computeKPIs());

  // Recompute KPIs on mount so date-based calculations use today's date
  useEffect(() => {
    setKpis(computeKPIs());
  }, []);

  // 8 cards → grid-cols-4 → 2 rows of 4
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {/* Row 1 */}
      <KPICard
        title="Total Vendors"
        value={kpis.totalVendors}
        subtext={`${kpis.criticalVendors} critical · ${kpis.highRiskVendors} high-risk`}
        icon={Building2}
        variant="neutral"
        href="/vendor-management"
      />
      <KPICard
        title="Critical Vendors"
        value={kpis.criticalVendors}
        subtext="Require quarterly review"
        icon={ShieldAlert}
        variant="info"
        href="/vendor-management"
      />
      <KPICard
        title="Contracts Expiring"
        value={kpis.expiring30 + kpis.expiring60 + kpis.expiring90}
        subtext={`${kpis.expiring30} ≤30d · ${kpis.expiring60} ≤60d · ${kpis.expiring90} ≤90d`}
        icon={FileText}
        variant={kpis.expiring30 > 0 ? 'warning' : 'neutral'}
        href="/vendor-management"
      />
      <KPICard
        title="Open Critical Issues"
        value={kpis.openCritical}
        subtext={`+ ${kpis.openHigh} high severity open`}
        icon={AlertTriangle}
        variant={kpis.openCritical > 0 ? 'critical' : 'positive'}
        href="/vendor-management"
      />
      {/* Row 2 */}
      <KPICard
        title="Missing Insurance"
        value={kpis.missingInsurance}
        subtext="COI expired or not received"
        icon={ShieldCheck}
        variant={kpis.missingInsurance > 0 ? 'critical' : 'positive'}
      />
      <KPICard
        title="DD In Progress"
        value={kpis.ddInProgress}
        subtext={`${kpis.overdueReassessments} overdue`}
        icon={ClipboardList}
        variant={kpis.overdueReassessments > 0 ? 'warning' : 'neutral'}
        href="/due-diligence-workspace"
      />
      <KPICard
        title="Overdue Reassessments"
        value={kpis.overdueReassessments}
        subtext="Past scheduled review date"
        icon={Clock}
        variant={kpis.overdueReassessments > 0 ? 'warning' : 'positive'}
        href="/due-diligence-workspace"
      />
      <KPICard
        title="Pending Intake"
        value={kpis.pendingIntake}
        subtext="Awaiting initial review"
        icon={TrendingUp}
        variant={kpis.pendingIntake > 0 ? 'info' : 'neutral'}
        href="/vendor-management"
      />
    </div>
  );
}